import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getRescueSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveRescue,
  setTypes,
  setHydraulics,
  setTools,
  setRehab,
  setActivations,
  setSize,
  setUpdateInputRows,
} from '../store/rescue.actions'
import { getRescueOptionsSelector } from '../../../options/selectors'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { RescueConstants } from '../store/rescue.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialRescueState } from '../store/rescue.reducers'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const Rescue: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const rescue = useSelector(getRescueSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    rescueTypes,
    hydraulics,
    tools,
    rehab,
    activations,
    size,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = rescue
  const rescueOptions = useSelector(getRescueOptionsSelector)
  const { typesOptions, hydraulicsOptions, toolsOptions, rehabOptions, activationsOptions } = rescueOptions
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const save = () => {
    dispatch(saveRescue())
  }

  const sizeOnChange = (values: string[]) => {
    dispatch(setSize(values.join('')))
    save()
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(rescue, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialRescueState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    const dateTimeValidationCheck = initialStateCheck()

    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'rescue/SET_TIME', time: time })
              dispatch({ type: 'rescue/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: rescue.key,
                rowActionType: TimelineConstants.SET_RESCUE_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: RescueConstants.SET_TYPES,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('rescue.types'),
            dataTestID: 'Types',
            options: typesOptions,
            action: setTypes,
            listHeader: 'Types',
            savedValues: rescueTypes,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: RescueConstants.SET_HYDRAULICS,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('rescue.hydraulics'),
            dataTestID: 'Hydraulics',
            options: hydraulicsOptions,
            action: setHydraulics,
            listHeader: 'Hydraulics',
            savedValues: hydraulics,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: RescueConstants.SET_TOOLS,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('rescue.tools'),
            dataTestID: 'Tools',
            options: toolsOptions,
            action: setTools,
            listHeader: 'Tools',
            savedValues: tools,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: RescueConstants.SET_REHAB,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('rescue.rehab'),
            dataTestID: 'Rehab',
            options: rehabOptions,
            action: setRehab,
            listHeader: 'Rehab',
            savedValues: rehab,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: RescueConstants.SET_ACTIVATIONS,
          type: ProcedureConstants.MULTI_SELECT,
          hidden: false,
          props: {
            label: t('rescue.activations'),
            dataTestID: 'Activations',
            options: activationsOptions,
            action: setActivations,
            listHeader: 'Activations',
            savedValues: activations,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: RescueConstants.SET_SIZE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('rescue.size'),
            dataTestId: 'Size',
            elementId: 'Size',
            values: size.split(''),
            onNumPadClick: sizeOnChange,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='RescuePage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Rescue</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'rescue'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default Rescue
